import type { IssueField, IssueForm, PresetId } from './types';
import { createUid } from './utils';

export interface PresetSummary {
  id: PresetId;
  name: string;
  description: string;
}

export const PRESETS: readonly PresetSummary[] = [
  {
    id: 'bug-report',
    name: 'Bug Report',
    description: 'Report a reproducible problem.',
  },
  {
    id: 'feature-request',
    name: 'Feature Request',
    description: 'Suggest an improvement or new capability.',
  },
  {
    id: 'documentation',
    name: 'Documentation Issue',
    description: 'Report missing, unclear, or incorrect documentation.',
  },
] as const;

function field<T extends Omit<IssueField, 'uid'>>(value: T): T & { uid: string } {
  return { uid: createUid('field'), ...value };
}

function option(label: string, required: boolean) {
  return { uid: createUid('option'), label, required };
}

function createBugReport(): IssueForm {
  return {
    uid: createUid('form'),
    filename: 'bug-report.yml',
    name: 'Bug Report',
    description: 'Report a reproducible problem so we can improve the project.',
    title: '[Bug]: ',
    labels: ['bug', 'needs-triage'],
    assignees: [],
    body: [
      field({
        type: 'markdown',
        attributes: {
          value:
            'Thanks for taking the time to file a bug report. Please provide enough detail for us to reproduce it.',
        },
      }),
      field({
        type: 'input',
        id: 'version',
        attributes: {
          label: 'IssueMint version',
          description: 'Which version are you using?',
          placeholder: 'v0.1.0',
          value: '',
        },
        validations: { required: true },
      }),
      field({
        type: 'textarea',
        id: 'description',
        attributes: {
          label: 'What happened?',
          description: 'Describe the problem and what you expected to happen.',
          placeholder: 'A clear and concise description of the bug.',
          value: '',
          render: '',
        },
        validations: { required: true },
      }),
      field({
        type: 'textarea',
        id: 'reproduction',
        attributes: {
          label: 'Steps to reproduce',
          description: 'List the smallest sequence of steps that triggers the problem.',
          placeholder: '1. Open…\n2. Select…\n3. Observe…',
          value: '',
          render: '',
        },
        validations: { required: true },
      }),
      field({
        type: 'dropdown',
        id: 'browser',
        attributes: {
          label: 'Browser',
          description: 'Where did the problem occur?',
          options: ['Chrome', 'Firefox', 'Safari', 'Edge', 'Other'],
          multiple: false,
        },
        validations: { required: true },
      }),
      field({
        type: 'checkboxes',
        id: 'checks',
        attributes: {
          label: 'Pre-submission checklist',
          description: 'Please confirm the following before submitting.',
          options: [
            option('I searched for existing issues that describe this problem.', true),
            option('I removed sensitive information from this report.', true),
          ],
        },
      }),
    ],
  };
}

function createFeatureRequest(): IssueForm {
  return {
    uid: createUid('form'),
    filename: 'feature-request.yml',
    name: 'Feature Request',
    description: 'Suggest an idea that would improve the project.',
    title: '[Feature]: ',
    labels: ['enhancement'],
    assignees: [],
    body: [
      field({
        type: 'markdown',
        attributes: {
          value:
            'Thanks for helping us improve. Explain the problem first, then describe the solution you have in mind.',
        },
      }),
      field({
        type: 'textarea',
        id: 'problem',
        attributes: {
          label: 'Problem to solve',
          description: 'What is difficult today, and who does it affect?',
          placeholder: 'When I…, I need…, so that…',
          value: '',
          render: '',
        },
        validations: { required: true },
      }),
      field({
        type: 'textarea',
        id: 'solution',
        attributes: {
          label: 'Proposed solution',
          description: 'Describe the outcome you would like.',
          placeholder: 'It would be useful if…',
          value: '',
          render: '',
        },
        validations: { required: true },
      }),
      field({
        type: 'textarea',
        id: 'alternatives',
        attributes: {
          label: 'Alternatives considered',
          description: 'What workarounds or alternatives have you tried?',
          placeholder: '',
          value: '',
          render: '',
        },
        validations: { required: false },
      }),
      field({
        type: 'checkboxes',
        id: 'feature-checks',
        attributes: {
          label: 'Checklist',
          description: '',
          options: [option('This request describes one focused improvement.', true)],
        },
      }),
    ],
  };
}

function createDocumentationIssue(): IssueForm {
  return {
    uid: createUid('form'),
    filename: 'documentation.yml',
    name: 'Documentation Issue',
    description: 'Report documentation that is missing, inaccurate, or unclear.',
    title: '[Docs]: ',
    labels: ['documentation'],
    assignees: [],
    body: [
      field({
        type: 'input',
        id: 'page',
        attributes: {
          label: 'Page or section',
          description: 'Share the relevant page title or URL.',
          placeholder: 'Getting started / Installation',
          value: '',
        },
        validations: { required: true },
      }),
      field({
        type: 'dropdown',
        id: 'kind',
        attributes: {
          label: 'Type of documentation issue',
          description: '',
          options: ['Missing information', 'Incorrect information', 'Unclear wording', 'Typo'],
          multiple: false,
        },
        validations: { required: true },
      }),
      field({
        type: 'textarea',
        id: 'details',
        attributes: {
          label: 'Details',
          description: 'Explain what should change and why.',
          placeholder: '',
          value: '',
          render: '',
        },
        validations: { required: true },
      }),
      field({
        type: 'textarea',
        id: 'suggestion',
        attributes: {
          label: 'Suggested wording',
          description: 'Optional: propose replacement text.',
          placeholder: '',
          value: '',
          render: 'markdown',
        },
        validations: { required: false },
      }),
    ],
  };
}

export function getPreset(id: PresetId): IssueForm {
  if (id === 'bug-report') return createBugReport();
  if (id === 'feature-request') return createFeatureRequest();
  return createDocumentationIssue();
}

export function getDefaultForm(): IssueForm {
  return getPreset('bug-report');
}
